// What kind of character is this, and is it actually missing its geometry?
//
// The launcher needs this per character BEFORE the user commits to a load. It
// cannot be answered from the character list alone: `renderer` comes back as ""
// for almost every row (the asset-manager strips the field — see the comment on
// isSplatCharacter in characterState.js), and it is only populated at runtime by
// applyCharacterPack, for the ACTIVE character only.
//
// Reading `renderer` off the list therefore mislabels every OGS/splat character
// as a rig character, and — because a splat character legitimately has no rig
// GLB — flags it as "no rig" when nothing is wrong with it. A false broken badge
// is worse than no badge, so the real answer needs the same /ogs probe
// CheckMissingRig does. That probe is what `probeRigStatuses` runs.
//
// Until a probe resolves, the status is 'unknown' and the UI shows NO claim.

import { checkForRig } from './checkForRig.js'
import { hasOgsBundleMarker } from './ogsContentSelect.js'

// Mirrors isStreamCharacter() in characterState.js rather than importing it:
// that module reaches devServer → convorcherTargets and assetManagerApi →
// apiClient → aamAssetCache, none of which resolve under `node --test` (they use
// extensionless specifiers). Keeping this module a leaf is what makes it
// unit-testable. The test is a single documented server-truth field, so the
// drift risk is small — but if the canonical one grows a second condition, this
// must follow.
const isStream = (character) => character?.renderer === 'stream'

/**
 * Classify one character given its /ogs rows.
 *
 * @param {object} character
 * @param {Array|null|undefined} ogsRows  rows from GET /api/characters/{id}/ogs.
 *   Pass `undefined` when no probe has run — the result is then only trustworthy
 *   for characters that DO have a rigPath.
 * @returns {'stream'|'splat'|'rig'|'missing'}
 */
export function classifyRigStatus(character, ogsRows) {
  if (!character) return 'missing'
  // Streaming is a server-truth property that IS carried on the row.
  if (isStream(character)) return 'stream'
  // An OGS bundle means renderable geometry even with no rig GLB.
  if (hasOgsBundleMarker(ogsRows)) return 'splat'
  if (checkForRig(character)) return 'rig'
  return 'missing'
}

/**
 * True when the classification can be trusted without an /ogs probe.
 *
 * A character WITH a rigPath is definitively a rig character — no probe adds
 * anything. A character WITHOUT one is ambiguous (rigless rig-character, or an
 * OGS bundle) and must be probed before any claim is made.
 */
export function isRigStatusDecidable(character) {
  if (!character) return false
  return isStream(character) || checkForRig(character)
}

/**
 * Probe every character that needs one and return a Map of id → status.
 *
 * Only the ambiguous characters (no rigPath, not streaming) cost a request, so a
 * catalog of ordinary rigged characters costs nothing. Probes run in parallel and
 * a failed probe resolves to 'unknown' rather than guessing 'missing' — a
 * network blip must not paint a working character as broken.
 *
 * @param {Array<object>} characters
 * @param {(id: string) => Promise<Array>} fetchOgsRows  injected for tests
 * @returns {Promise<Map<string, 'stream'|'splat'|'rig'|'missing'|'unknown'>>}
 */
export async function probeRigStatuses(characters, fetchOgsRows) {
  const out = new Map()
  if (!Array.isArray(characters)) return out

  const ambiguous = []
  for (const c of characters) {
    if (!c?.id) continue
    if (isRigStatusDecidable(c)) out.set(c.id, classifyRigStatus(c, null))
    else ambiguous.push(c)
  }

  await Promise.all(ambiguous.map(async (c) => {
    let rows
    try {
      rows = await fetchOgsRows(c.id)
    } catch {
      // Unknown, not missing. The UI shows no badge for 'unknown'.
      out.set(c.id, 'unknown')
      return
    }
    out.set(c.id, classifyRigStatus(c, rows))
  }))

  return out
}
